import React from 'react';
import { FileText, Upload, Search, Shield, AlertCircle, CheckCircle2, Loader2, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { documents, matters as mattersApi, API } from '@/lib/api';

export default function DocumentsPage() {
  const [docs, setDocs] = React.useState([]);
  const [ms, setMs] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([documents.list(), mattersApi.list()])
      .then(([d, m]) => { setDocs(d); setMs(m); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  const filtered = docs.filter((d) => !q || `${d.file_name} ${d.doc_type} ${d.matter_ref}`.toLowerCase().includes(q.toLowerCase()));

  const sign = async (d) => { await documents.sign(d.id); load(); };
  const remove = async (d) => { if (window.confirm(`Delete ${d.file_name}?`)) { await documents.remove(d.id); load(); } };

  const download = async (d) => {
    // Use axios-authed download
    const res = await API.get(`/documents/${d.id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: d.content_type || 'application/octet-stream' }));
    const a = document.createElement('a');
    a.href = url; a.download = d.file_name; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Document Vault</h1>
          <div className="text-sm text-slate-500">Real uploads to MongoDB GridFS · WORM audit log · client statutory declaration</div>
        </div>
        <UploadDialog matters={ms} onUploaded={load} />
      </div>

      <div className="mt-6 relative max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents, matters, types…" className="h-10 pl-9" />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2ECC9A]"><Shield className="h-3.5 w-3.5" /> Client declaration</div>
          <div className="mt-2 text-sm text-slate-700">Every document requires the client-signed “true &amp; not fraudulent” declaration.</div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2ECC9A]"><FileText className="h-3.5 w-3.5" /> Auto-classification</div>
          <div className="mt-2 text-sm text-slate-700">Uploads are auto-tagged by filename (passport, ACS, IELTS, police, etc.).</div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2ECC9A]"><CheckCircle2 className="h-3.5 w-3.5" /> 7-year retention</div>
          <div className="mt-2 text-sm text-slate-700">Documents retained 7 years from matter close, then auto-purged with return-of-originals pack.</div>
        </CardContent></Card>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <FileText className="h-8 w-8 text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">No documents yet. Upload one to get started.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">File</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Matter</th>
                <th className="px-4 py-3 text-left">Size</th>
                <th className="px-4 py-3 text-left">Uploaded</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((d) => (
                <tr key={d.id} className="transition-colors hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-[#1A3A5C]">{d.file_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="secondary" className="bg-slate-100 text-slate-700">{d.doc_type}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{d.matter_ref || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{(d.size_bytes / 1024).toFixed(1)} KB</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{new Date(d.uploaded_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {d.status === 'verified' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Verified</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><AlertCircle className="h-3 w-3" /> Pending declaration</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" onClick={() => download(d)} className="h-8 w-8 p-0" title="Download"><Download className="h-3.5 w-3.5" /></Button>
                      {d.status !== 'verified' && (
                        <DeclarationDialog doc={d} onSigned={load} />
                      )}
                      <Button variant="ghost" onClick={() => remove(d)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700" title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function UploadDialog({ matters, onUploaded }) {
  const [open, setOpen] = React.useState(false);
  const [matterId, setMatterId] = React.useState(matters[0]?.id || '');
  const [file, setFile] = React.useState(null);
  const [docType, setDocType] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  React.useEffect(() => { if (matters.length > 0 && !matterId) setMatterId(matters[0].id); }, [matters, matterId]);

  const submit = async (e) => {
    e.preventDefault(); if (!file || !matterId) return;
    setErr(''); setSaving(true);
    try { await documents.upload(matterId, file, docType); setOpen(false); setFile(null); setDocType(''); onUploaded?.(); }
    catch (er) { setErr(er?.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-10 gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"><Upload className="h-4 w-4" /> Upload document</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Upload document to matter</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Matter</Label>
            <Select value={matterId} onValueChange={setMatterId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.ref} — {m.applicant}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Document type (optional — will auto-classify by name)</Label>
            <Input value={docType} onChange={(e)=>setDocType(e.target.value)} placeholder="Passport, Skills Assessment, Police Check…" className="mt-1" />
          </div>
          <div><Label>File</Label>
            <Input type="file" required onChange={(e)=>setFile(e.target.files?.[0] || null)} className="mt-1" />
            <div className="mt-1 text-xs text-slate-500">Max 15 MB. Accepted: PDF, images, DOCX.</div>
          </div>
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}
          <DialogFooter>
            <Button type="submit" disabled={saving || !file} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Upload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeclarationDialog({ doc, onSigned }) {
  const [open, setOpen] = React.useState(false);
  const [signed, setSigned] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    setSaving(true);
    try { await documents.sign(doc.id); setOpen(false); setSigned(false); onSigned?.(); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0 text-[#2ECC9A]" title="Sign declaration"><Shield className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Client statutory declaration</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm text-slate-700">
          <div className="rounded-md bg-[#F4F7FA] p-4">
            The client for matter <strong>{doc.matter_ref}</strong> solemnly declares that the file <strong>{doc.file_name}</strong> is true, correct, complete and not fraudulent, and that this declaration is made under the Statutory Declarations Act 1959 (Cth).
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="ack" checked={signed} onCheckedChange={(v) => setSigned(!!v)} />
            <label htmlFor="ack" className="text-xs text-slate-600 cursor-pointer">Client&rsquo;s e-signature has been captured and this declaration is being logged on their behalf.</label>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!signed || saving} onClick={submit} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} E-sign &amp; timestamp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
